import dayjs from 'dayjs';
import {
  BookingStatus,
  ClientType,
  FeaturesEnum,
  FileType,
  FileTypeEnum,
  OwnerType,
  RideOfferType,
  ScheduleItemType,
  UserRole,
  clientDtoSchema,
  contactInfoDtoSchema,
  locationBaseSchema,
  zo,
} from 'remote/global';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { TokenStatus } from 'utils/data/enums';

export const fileTypeObject = zo.object({
  fileName: zo.string(),
  fileSize: zo.number().max(2_000_000),
  fileType: zo.nativeEnum(FileTypeEnum),
  originalFileName: zo.string(),
  url: zo.string(),
  originalFile: zo.instanceof(File).optional(),
}) satisfies zo.ZodType<FileType>;

const breakItem = {
  start: '',
  end: '',
};

const breakSchema = zo.object({
  start: zo.string().datetime(),
  end: zo.string().datetime(),
});

const scheduleItemSchema = zo
  .object({
    ownerId: zo.number(),
    startTime: zo.string().min(1).datetime(),
    endTime: zo.string().min(1).datetime(),
    itemType: zo.nativeEnum(ScheduleItemType),
    ownerType: zo.nativeEnum(OwnerType),
    breaks: zo.array(breakSchema).optional(),
  })
  .superRefine((args, ctx) => {
    const { endTime, startTime } = args;

    // endTime can't be earlier than startTime
    if (dayjs(endTime) <= dayjs(startTime)) {
      ['startTime', 'endTime'].map((path, idx) =>
        ctx.addIssue({
          code: zo.ZodIssueCode.custom,
          params: {
            i18n: {
              key: idx === 0 ? 'dateAfterAnother_with_value' : 'dateBeforeAnother_with_value',
              values: {
                other:
                  idx === 0
                    ? 'paths.repairScheduleItem.endTime'
                    : 'paths.repairScheduleItem.startTime',
              },
            },
          },
          path: [path],
        })
      );
    }
    // Validate each break's start and end are within the startTime and endTime range
    args.breaks?.forEach((item, index) => {
      const breakStart = dayjs(item.start);
      const breakEnd = dayjs(item.end);
      if (breakStart.isBefore(dayjs(startTime).add(1, 'minute'))) {
        ctx.addIssue({
          code: zo.ZodIssueCode.custom,
          params: {
            i18n: {
              key: 'scheduleItemDateTooEarly',
            },
          },
          path: ['breaks', index, 'start'],
        });
      }
      if (breakEnd.isAfter(dayjs(endTime).subtract(1, 'minute'))) {
        ctx.addIssue({
          code: zo.ZodIssueCode.custom,
          params: {
            i18n: {
              key: 'scheduleItemDateTooLate',
            },
          },
          path: ['breaks', index, 'end'],
        });
      }
      if (breakStart.isAfter(breakEnd.subtract(1, 'minute'))) {
        ctx.addIssue({
          code: zo.ZodIssueCode.custom,
          params: {
            i18n: {
              key: 'startDateSmallerThanEnd',
            },
          },
          path: ['breaks', index, 'start'],
        });
        ctx.addIssue({
          code: zo.ZodIssueCode.custom,
          params: {
            i18n: {
              key: 'endDateGreaterThanStart',
            },
          },
          path: ['breaks', index, 'end'],
        });
      }
    });
  });
type ScheduleItemSchemaType = zo.infer<typeof scheduleItemSchema>;
type BreakSchemaType = zo.infer<typeof breakSchema>;

const scheduleItemDtoSchema = scheduleItemSchema.and(
  zo.object({
    bookingNumber: zo.string().optional(),
    bookingStatus: zo.nativeEnum(BookingStatus).optional(),
    clientEndAddress: zo.string().optional(),
    clientEndTime: zo.string().datetime().optional(),
    clientStartAddress: zo.string().optional(),
    clientStartTime: zo.string().datetime().optional(),
    comment: zo.string().optional(),
    features: zo.nativeEnum(FeaturesEnum),
    id: zo.number().optional(),
    itemType: zo.nativeEnum(ScheduleItemType),
    movable: zo.boolean(),
    operatorId: zo.number(),
    orderId: zo.number().optional(),
    orderType: zo.nativeEnum(RideOfferType),
    rideId: zo.number().optional(),
    rideNumber: zo.number().optional(),
  })
);
type ScheduleItemDtoSchemaType = zo.infer<typeof scheduleItemDtoSchema>;

const restScheduleItemSchema = scheduleItemSchema.and(
  zo.object({
    resourceId: zo.number().or(zo.undefined()),
  })
);

const repairScheduleItemSchema = scheduleItemSchema.and(
  zo.object({
    resourceId: zo.number().or(zo.undefined()),
  })
);

const offlineScheduleItemSchema = scheduleItemSchema.and(
  zo.object({
    resourceId: zo.number().or(zo.undefined()),
  })
);

enum OfferApprovalTypeEnum {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL',
}

const operatorDtoSchema = zo.object({
  address: zo.string().optional(),
  approved: zo.boolean(),
  brandName: zo.string(),
  companyVat: zo.string().optional(),
  contactEmail: zo.string().optional(),
  contactPhoneNumber: zo.string().optional(),
  country: zo.string(),
  description: zo.string().optional(),
  emergencyEmail: zo.string().optional(),
  emergencyPhoneNumber: zo.string().optional(),
  id: zo.number(),
  legalName: zo.string(),
  name: zo.string().optional(),
  type: zo.nativeEnum(ClientType),
  offerApprovalType: zo.nativeEnum(OfferApprovalTypeEnum),
  operatingLicense: zo.string(),
  regCode: zo.string(),
  skipCancellationPenalty: zo.boolean(),
  termsAcceptedTime: zo.string().datetime(),
  website: zo.string().optional(),
  offerContactEmail: zo.string().min(1).email().or(zo.literal('')).optional(),
  offerContactPhone: zo
    .string()
    .min(1)
    .refine((val) =>
      isValidPhoneNumber(val, {
        defaultCountry: 'EE',
        defaultCallingCode: '372',
      })
    )
    .or(zo.literal(''))
    .optional(),
});
type OperatorDtoSchemaType = zo.infer<typeof operatorDtoSchema>;

const tokenStatusResponseSchema = zo.object({
  email: zo.string().optional(),
  role: zo.nativeEnum(UserRole).optional(),
  status: zo.nativeEnum(TokenStatus),
});
type TokenStatusResponseSchemaType = zo.infer<typeof tokenStatusResponseSchema>;

const registrationResponseSchema = zo.object({
  client: clientDtoSchema.optional(),
  contactInfo: contactInfoDtoSchema,
  jwt: zo.string(),
  operator: operatorDtoSchema,
  refreshToken: zo.string(),
  userId: zo.number(),
});
type RegistrationResponseSchemaType = zo.infer<typeof registrationResponseSchema>;

type RepairScheduleItemSchemaType = zo.infer<typeof repairScheduleItemSchema>;
type OfflineScheduleItemSchemaType = zo.infer<typeof offlineScheduleItemSchema>;
type RestScheduleItemSchemaType = zo.infer<typeof restScheduleItemSchema>;

const vehicleTemporaryBaseDtoSchema = zo.object({
  id: zo.number().optional(),
  location: locationBaseSchema,
  validFrom: zo.string().datetime(),
  validTo: zo.string().datetime(),
});
type VehicleTemporaryBaseDtoSchemaType = zo.infer<typeof vehicleTemporaryBaseDtoSchema>;

const sendOfferToClientRequestSchema = zo.object({
  clientEmail: zo.string(),
  lang: zo.string().optional(),
  operatorOfferUuids: zo.array(zo.string()),
  reserveVehicle: zo.boolean(),
  validUntil: zo.string().date().optional(),
  refNumber: zo.string(), // only used internally
});
type SendOfferToClientRequestSchemaType = zo.infer<typeof sendOfferToClientRequestSchema>;

export {
  repairScheduleItemSchema,
  restScheduleItemSchema,
  scheduleItemDtoSchema,
  offlineScheduleItemSchema,
  breakItem,
};
export type {
  RepairScheduleItemSchemaType,
  RestScheduleItemSchemaType,
  ScheduleItemSchemaType,
  OperatorDtoSchemaType,
  TokenStatusResponseSchemaType,
  RegistrationResponseSchemaType,
  ScheduleItemDtoSchemaType,
  VehicleTemporaryBaseDtoSchemaType,
  SendOfferToClientRequestSchemaType,
  OfflineScheduleItemSchemaType,
  BreakSchemaType,
};
